/*
 * Every icon the app uses, in one place.
 *
 * V1 uses FontAwesome **Pro** (`pro-regular`, `pro-solid`). We are on the free
 * set until the Pro registry token is available, which means a handful of
 * glyphs are near-equivalents rather than the exact ones a pharmacist sees in
 * V1 today. Each substitution is marked `SUBSTITUTE` below.
 *
 * This module exists so that swap is one file and not a search across the app:
 * when the token arrives, change the two imports to `pro-regular-svg-icons`
 * and `pro-solid-svg-icons`, restore the names marked below, and nothing else
 * moves. See issue #76.
 */
import {
  faCopy,
  faDownload,
  faEraser,
  faThumbtack,
  faTrashCan,
  faPaperclip,
  faArrowLeft,
  faArrowRight,
  faArrowsRotate,
  faCheck,
  faChevronDown,
  faCircleCheck,
  faCircleExclamation,
  faCommentDots,
  faCircleInfo,
  faCircleQuestion,
  faEye,
  faMagnifyingGlass,
  faPrescription,
  faRightFromBracket,
  faRotateRight,
  faSpellCheck,
  faSpinner,
  faTriangleExclamation,
  faUser,
  faXmark,
  faFlask,
  faBookmark,
  faCode,
  faPlus,
  faPrint,
  faUserPlus,
  faScaleBalanced,
  faListCheck,
  faRoute,
  faFilter,
  faDesktop,
  faNotEqual,
} from '@fortawesome/free-solid-svg-icons'

/**
 * Named by role rather than by glyph, so a screen asks for `icons.undecided`
 * and never for `faCircleQuestion`. It also means the Pro swap cannot change
 * what a screen means -- only what it looks like.
 */
export const icons = {
  // Navigation
  back: faArrowLeft,
  forward: faArrowRight,
  /** Opens something in place. */
  expand: faChevronDown,
  search: faMagnifyingGlass,
  close: faXmark,
  logout: faRightFromBracket,
  user: faUser,

  // The four dispositions. These carry meaning a pharmacist acts on, so they
  // are the substitutions to check first when Pro lands.
  ok: faCircleCheck,
  /**
   * The free set does carry `faCircleInfo` -- only V1's lighter pro-regular
   * weight is missing, so this is no longer a substitute glyph. Moving it off
   * `faCircleQuestion` also frees the question mark for `undecided`, which is
   * the thing it actually means.
   */
  info: faCircleInfo,
  review: faTriangleExclamation,
  /**
   * The enclosed mark, for flagging inside a dense row where a triangle shouts.
   *
   * SUBSTITUTE: the intended icon is pro-solid `faSquareExclamation`. Free
   * carries no square-exclamation, and the circle is the better of the two
   * available shapes anyway -- it is the same enclosure as `ok`'s
   * `faCircleCheck`, and these two are drawn beside each other in the queue's
   * apontamentos column. A bare `!` next to a circled ✓ read as an unfinished
   * glyph rather than a pair.
   */
  alert: faCircleExclamation,
  /**
   * A simulation.
   *
   * A flask, because that is what the pharmacy's own bench uses for something
   * being tried rather than dispensed -- and because every other candidate
   * (a beaker, a wand, a test tube) either reads as chemistry we are not doing
   * or as magic we are certainly not doing.
   */
  simulation: faFlask,
  /** Keeping a conferência as a golden-set case. */
  goldenSet: faBookmark,
  /**
   * The adversarial review (#541): a second opinion on how we read and judged.
   *
   * A balance, because it weighs our answer against another one. Drawn for an
   * operator, like the two beside it, and never for a pharmacist.
   */
  secondOpinion: faScaleBalanced,
  /** The review's result: the list of lines it disputes. */
  reviewResult: faListCheck,
  /** The review's guided tour: from one disputed row to the next. */
  reviewTour: faRoute,
  /**
   * What the leitor answered, as the object itself.
   *
   * Braces, because that is what it is. This one is drawn for an operator
   * debugging the reader, never for a pharmacist conferring a receita, and it
   * should look like the machine's own answer rather than like another
   * apontamento.
   */
  readerObject: faCode,
  /**
   * "Nobody could tell", which is a different thing from "this is wrong".
   *
   * SUBSTITUTE: V1 uses pro-solid `faOctagonExclamation`, which is Pro-only.
   * This used to fall back to `faCircleExclamation` -- the same glyph as
   * `alert` -- so the two dispositions the domain is most careful to keep
   * apart were drawn identically, and a row saying *we could not compare the
   * dose* looked exactly like one saying *the dose is wrong*.
   *
   * A question mark is the honest shape for it, and it is the one enclosure
   * left that is not already spoken for. When Pro lands, restore the octagon:
   * the shape matters more than which mark is inside it.
   */
  undecided: faCircleQuestion,

  // The two panels of a conference.
  /**
   * Saying something about the conferência itself, rather than about one of
   * its findings. Dots inside the bubble, because it is words somebody wrote.
   */
  feedback: faCommentDots,
  /** SUBSTITUTE: V1 draws a pot; the free set's closest is the Rx mark. */
  prescription: faPrescription,
  comparison: faSpellCheck,
  /** Walking a conferência by its divergências alone (#641). */
  divergenceOnly: faFilter,
  /** What was keyed in the ERP, beside the receita's Rx (#641). */
  system: faDesktop,
  /** Between the two sides of one divergence (#641). */
  notEqual: faNotEqual,

  // Actions
  confirm: faCheck,
  /** Run it again. */
  refresh: faArrowsRotate,
  /** Show this one on the prescription, and only this one. */
  locate: faEye,
  remove: faTrashCan,
  /** Creating a new record from a list. */
  add: faPlus,
  invite: faUserPlus,
  /** Attach a file: a receita the ERP does not have (#276). */
  attach: faPaperclip,
  clear: faEraser,
  copy: faCopy,
  download: faDownload,
  print: faPrint,
  /* Distinct from `locate`: this one holds a region on the page rather than
     jumping to it, and the pharmacist's viewer keeps the eye. */
  pin: faThumbtack,
  /** Turning a sideways photograph upright. */
  rotate: faRotateRight,
  busy: faSpinner,
} as const

export type IconName = keyof typeof icons
